import { useMemo, useState } from 'react'
import { useShopI18n } from '../../i18n'
import type { CheckoutCartSnapshotItem, CheckoutSessionStatus } from '../types'

type TotalsRow = {
  label: string
  value: string
  strong?: boolean
}

type Props = {
  items: CheckoutCartSnapshotItem[]
  totalsRows: TotalsRow[]
  sessionStatus: CheckoutSessionStatus | null
  isSubmitting: boolean
  formatCurrency: (value: number) => string
  onBackToCart: () => void
}

const resolveStatusTone = (status: CheckoutSessionStatus | null) => {
  if (status === 'expired' || status === 'cancelled') {
    return 'border-rose-200 bg-rose-50 text-rose-800'
  }
  if (status === 'payment_pending') {
    return 'border-amber-200 bg-amber-50 text-amber-800'
  }
  return 'border-slate-200 bg-slate-50 text-slate-700'
}

export const CheckoutOrderSummary = ({
  items,
  totalsRows,
  sessionStatus,
  isSubmitting,
  formatCurrency,
  onBackToCart,
}: Props) => {
  const { t } = useShopI18n()
  const [mobileOpen, setMobileOpen] = useState(false)

  const statusLabel = useMemo(() => {
    if (!sessionStatus) return null
    if (sessionStatus === 'payment_pending') return t('checkout.summary.statusPending')
    if (sessionStatus === 'expired') return t('checkout.summary.statusExpired')
    if (sessionStatus === 'cancelled') return t('checkout.summary.statusCancelled')
    return null
  }, [sessionStatus, t])

  return (
    <aside className="rounded-2xl border border-slate-200 bg-white shadow-sm xl:sticky xl:top-24" aria-label={t('checkout.summary.title')}>
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <h2 className="text-sm font-semibold uppercase tracking-[0.12em] text-slate-700">{t('checkout.summary.title')}</h2>
        <button
          type="button"
          className="text-xs font-semibold text-slate-700 underline underline-offset-2 xl:hidden"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((current) => !current)}
        >
          {mobileOpen ? t('checkout.summary.hide') : t('checkout.summary.show')}
        </button>
      </div>

      <div className={`${mobileOpen ? 'block' : 'hidden'} space-y-4 p-4 xl:block`}>
        {statusLabel ? (
          <p className={`rounded-lg border px-3 py-2 text-xs font-medium ${resolveStatusTone(sessionStatus)}`}>
            {statusLabel}
          </p>
        ) : null}

        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.itemId} className="rounded-lg border border-slate-100 px-3 py-2">
              <p className="line-clamp-1 text-sm font-medium text-slate-900">{item.productName}</p>
              <div className="mt-1 flex items-center justify-between text-xs text-slate-600">
                <span>x{item.quantity}</span>
                <span>{formatCurrency(item.lineTotal)}</span>
              </div>
            </li>
          ))}
        </ul>

        <div className="space-y-1 rounded-lg border border-slate-100 bg-slate-50 p-3 text-sm">
          {totalsRows.map((row) => (
            <div key={row.label} className={`flex items-center justify-between ${row.strong ? 'font-semibold text-slate-900' : 'text-slate-700'}`}>
              <span>{row.label}</span>
              <span>{row.value}</span>
            </div>
          ))}
        </div>

        <div className="rounded-lg border border-sky-100 bg-sky-50 px-3 py-2 text-xs text-sky-900">
          <p className="font-semibold">{t('checkout.summary.securityTitle')}</p>
          <p className="mt-1">{t('checkout.summary.securityBody')}</p>
        </div>

        <button
          type="button"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500"
          onClick={onBackToCart}
          disabled={isSubmitting}
        >
          {t('checkout.cta.backToCart')}
        </button>
      </div>
    </aside>
  )
}
